import { useEffect, useState } from 'react';
import _ from 'lodash';
import { UserActions } from 'BMapsCmds';
import { useSelector } from 'BMapsRedux';
import { setSidePanelWidth } from 'BMapsRedux/projectState/access';
import { useColorSchemeInfo } from 'BMapsRedux/prefs/access';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { SidePanelContainer } from 'BMapsUI/info_display/SidePanel';
import TabController from 'BMapsUI/common/TabController';
import tabs from './Tabs';

const TABLE_ID = 'freeEnergyTable';
const DEFAULT_WIDTH = 760;

/**
 * The Free Energy side panel: submit an FEP job (New Job), watch and cancel
 * jobs (Jobs), and view ΔG / ΔΔG tables and apply ΔG to compounds (Results).
 * Structured like the Screening panel.
 */
export function FreeEnergySidePanel({
    open, handleClose, tabId='new_fep_tab', initData={},
}) {
    const tableState = useSelector((state) => state.projectState.tableState[TABLE_ID]) || {};
    const { sidePanelWidth = DEFAULT_WIDTH } = tableState;

    const { textCss } = useColorSchemeInfo();

    const [width, setWidth] = useState(sidePanelWidth);
    const [highlightedJobId, setHighlightedJobId] = useState(null);
    const [resizing, setResizing] = useState(false);

    function onMouseDown(e) {
        e.preventDefault();
        e.stopPropagation();
        setResizing(true);
    }

    function setTabId(tab) {
        openFreeEnergySidePanel(tab, initData);
    }

    useEffect(() => {
        // This 0 debounce prevents a "too many renders" error that can happen when resizing quickly
        const debouncedSetWidth = _.debounce((newWidth) => setWidth(newWidth), 0);
        function onMouseUp() {
            setResizing(false);
            setSidePanelWidth(width, TABLE_ID);
            EventBroker.publish('resize');
            debouncedSetWidth.cancel();
        }
        function onMouseMove(e) {
            e.preventDefault();
            e.stopPropagation();
            const newWidth = e.clientX - 10;
            if (newWidth > 20) {
                debouncedSetWidth(newWidth);
            }
        }
        if (resizing) {
            document.addEventListener('mouseup', onMouseUp);
            document.addEventListener('mousemove', onMouseMove);
            return () => {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
                debouncedSetWidth.cancel();
            };
        }
        return undefined;
    }, [resizing]);

    return (
        <div style={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
            <SidePanelContainer
                open={open}
                handleClose={handleClose}
                title="Free Energy"
                width={width}
                contentStyle={{
                    padding: '0.5rem',
                }}
            >
                <TabController
                    tabs={tabs.map((tab) => tab.forTabController(
                        {
                            initData,
                            highlightedJobId,
                            setHighlightedJobId,
                        }
                    ))}
                    selected={tabId}
                    setSelected={setTabId}
                    style={{
                        flex: 1,
                    }}
                />
            </SidePanelContainer>
            <div
                role="toolbar"
                onMouseDown={onMouseDown}
                style={{
                    cursor: 'ew-resize',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    marginRight: '.5rem',
                }}
            >
                <i className="fa fa-arrows-h" style={{ color: textCss }} />
            </div>
        </div>
    );
}

/**
 * Open (or re-target) the Free Energy side panel.
 * @param {'new_fep_tab'|'fep_jobs_tab'|'fep_results_tab'} tabId
 * @param {{ selectedCompounds?: import('BMapsModel').Compound[] }} [initData]
 */
export function openFreeEnergySidePanel(tabId, initData) {
    const validTabId = tabs.find((t) => t.id === tabId)?.id || tabs[0].id;
    UserActions.OpenSidePanel('left', {
        pageId: 'free_energy',
        props: {
            initData,
            tabId: validTabId,
        },
        component: <FreeEnergySidePanel />,
        longLived: false,
    });
}

// Accepts either the object form `{tabId, initData}` — which is how the Gibbs
// pre-fill handoff arrives (UICmds.openFreeEnergyPanel) — or a bare tab-id
// string, so callers that only want to switch tabs are unaffected. Same shape
// as the Screening panel's subscription.
EventBroker.subscribe('askOpenFreeEnergyPanel', (event, payload) => {
    if (payload && typeof payload === 'object') {
        openFreeEnergySidePanel(payload.tabId, payload.initData);
    } else {
        openFreeEnergySidePanel(payload);
    }
});
